// Production API configuration for FormCraft
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    // Use environment variable if available, otherwise derive from current domain
    const envApiUrl = import.meta.env.VITE_API_BASE_URL;
    if (envApiUrl) {
      return envApiUrl;
    }
    
    // For production, derive API URL from current domain
    // This allows it to work with any custom domain setup
    const currentHost = window.location.hostname;
    const apiHost = currentHost.replace(/^(forms\.|embed\.)/, 'api.');
    return `${window.location.protocol}//${apiHost}`;
  }
  return '';
};

const API_BASE_URL = getApiBaseUrl();

export interface Form {
  id: string;
  name: string;
  description?: string;
  status: 'draft' | 'published';
  created_at: string;
  fields?: Field[];
  submission_count?: number;
}

export interface Field {
  id: string;
  form_id: string;
  type: 'text' | 'email' | 'url' | 'number' | 'textarea' | 'select';
  label: string;
  placeholder?: string;
  required: boolean;
  validation_regex?: string;
  validation_message?: string;
  disabled_domains?: string[];
  field_options?: string[];
  order_index: number;
}

export interface FormSubmission {
  id: string;
  form_id: string;
  field_responses: Record<string, string>;
  submitted_at: string;
}

// Form APIs
export async function getForms(): Promise<Form[]> {
  const response = await fetch(`${API_BASE_URL}/api/forms`);
  if (!response.ok) throw new Error('Failed to fetch forms');
  return response.json();
}

export async function getForm(id: string): Promise<Form> {
  const response = await fetch(`${API_BASE_URL}/api/forms/${id}`);
  if (!response.ok) throw new Error('Failed to fetch form');
  return response.json();
}

export async function createForm(name: string, description?: string): Promise<Form> {
  const response = await fetch(`${API_BASE_URL}/api/forms`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description })
  });
  if (!response.ok) throw new Error('Failed to create form');
  return response.json();
}

export async function updateForm(id: string, data: Partial<Form & { fields: Field[] }>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/forms/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to update form');
}

export async function deleteForm(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/forms/${id}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to delete form');
}

export async function cloneForm(id: string): Promise<{ newFormId: string }> {
  const response = await fetch(`${API_BASE_URL}/api/forms/${id}/clone`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to clone form');
  return response.json();
}

export async function toggleFormStatus(id: string): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE_URL}/api/forms/${id}/toggle-status`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to toggle form status');
  return response.json();
}

export async function exportFormData(id: string): Promise<{ form: Form, submissions: FormSubmission[] }> {
  const response = await fetch(`${API_BASE_URL}/api/forms/${id}/export`);
  if (!response.ok) throw new Error('Failed to export form data');
  return response.json();
}

// Submission APIs
export async function submitResponse(formId: string, responses: Record<string, string>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/forms/${formId}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ field_responses: responses })
  });
  if (!response.ok) throw new Error('Failed to submit form');
}

export async function getSubmissions(formId: string): Promise<FormSubmission[]> {
  const response = await fetch(`${API_BASE_URL}/api/forms/${formId}/submissions`);
  if (!response.ok) throw new Error('Failed to fetch submissions');
  return response.json();
}